// Global Instructions Rule Applied! - March 31, 2026
import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faArrowDownWideShort, faTableColumns } from '@fortawesome/free-solid-svg-icons'
import { Badge } from 'antd'
import Button from '../Button.jsx'
import { EditColumnsDrawer } from './EditColumnsDrawer.tsx'
import type { ViewConfigDrawerMode } from './EditColumnsDrawer.tsx'
import type { AddColumnsCatalog } from './AddColumnsPanel.tsx'
import type { ColumnLayoutPersistenceProps } from './EditColumnsDrawer.tsx'
import type { FilteringPersistenceProps } from './EditColumnsDrawer.tsx'
import type { SortingPersistenceProps } from './EditColumnsDrawer.tsx'
import type { FilterOptionsPersistenceProps } from './EditColumnsDrawer.tsx'
import type { ViewConfigColumn } from './types.ts'
import type { FilterOptions } from '../TableView/TableViewTypes.ts'

export interface ViewConfigProps {
  moduleTitle: string
  columns: ViewConfigColumn[]
  className?: string
  showFilteringButton?: boolean
  showSortingButton?: boolean
  /** Matches TableView `filterOptions` (e.g. Redux `dealsFilterOptions.data`) */
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  /** Deal / Related column catalogs for Add columns (`reporting_modules` + `reporting_columns`) */
  addColumnsCatalog?: AddColumnsCatalog
  addColumnsTabLabels?: { primary: string; related: string }
  /** Persist column order / visibility to user preferences */
  columnLayout?: ColumnLayoutPersistenceProps
  filteringConfig?: FilteringPersistenceProps
  sortingConfig?: SortingPersistenceProps
  /** Optional Options-section contract above FilterPanel (controls + apply hooks). */
  filterOptionsConfig?: FilterOptionsPersistenceProps
}

export function ViewConfig({
  moduleTitle,
  columns,
  className = '',
  showFilteringButton = true,
  showSortingButton = true,
  filterOptions,
  filterOptionsLoading,
  addColumnsCatalog,
  addColumnsTabLabels,
  columnLayout,
  filteringConfig,
  sortingConfig,
  filterOptionsConfig
}: ViewConfigProps) {
  const currentFilters = filteringConfig?.currentFilters ?? []
  const activeFilterCount = currentFilters.length

  const currentSorting = sortingConfig?.currentSorting ?? []
  const activeSortCount = currentSorting.length

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<ViewConfigDrawerMode>('columns')

  const openDrawer = (mode: ViewConfigDrawerMode) => {
    setDrawerMode(mode)
    setDrawerOpen(true)
  }

  const toolbarBtnClass =
    '!border-0 !shadow-none !bg-transparent !text-white hover:!bg-white/10 hover:!text-white [&_.ant-btn-icon]:!text-white'

  return (
    <>
      <div className={`flex items-center gap-2 flex-wrap ${className}`}>
        {showFilteringButton && (
          <Badge count={activeFilterCount} size='small' offset={[0, 2]} showZero={false}>
            <Button
              variant='default'
              type='text'
              size='small'
              icon={<FontAwesomeIcon icon={faFilter} />}
              className={toolbarBtnClass}
              onClick={() => openDrawer('filters')}
            >
              Filtering
            </Button>
          </Badge>
        )}
        {showSortingButton && (
          <Badge count={activeSortCount} size='small' offset={[0, 2]} showZero={false}>
            <Button
              variant='default'
              type='text'
              size='small'
              icon={<FontAwesomeIcon icon={faArrowDownWideShort} />}
              className={toolbarBtnClass}
              onClick={() => openDrawer('sorting')}
            >
              Sorting
            </Button>
          </Badge>
        )}
        <Button
          variant='default'
          type='text'
          size='small'
          icon={<FontAwesomeIcon icon={faTableColumns} />}
          className={toolbarBtnClass}
          onClick={() => openDrawer('columns')}
        >
          Edit columns
        </Button>
      </div>

      <EditColumnsDrawer
        open={drawerOpen}
        mode={drawerMode}
        moduleTitle={moduleTitle}
        columns={columns}
        onClose={() => setDrawerOpen(false)}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        addColumnsCatalog={addColumnsCatalog}
        addColumnsTabLabels={addColumnsTabLabels}
        columnLayout={columnLayout}
        filteringConfig={filteringConfig}
        sortingConfig={sortingConfig}
        filterOptionsConfig={filterOptionsConfig}
      />
    </>
  )
}
